/**
 * InnerMirrorApp — 7-chapter reflection + Founding Devotee card + 5-slot
 * WhatsApp invite + 500-credit challenge, all behind the existing auth
 * guard. Mounted at `/inner-mirror`.
 *
 * Design: Apple-minimal, luminous purple/aqua, 1 question per screen.
 * The mirror ring in the top-right fills by one tick per completed chapter.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, useAuth } from "@/hooks/useAuth";
import KalkiGlobalHeader from "@/components/kalki/KalkiGlobalHeader";
import { CHAPTERS, RELATIONSHIP_SLOTS, INVITE_SLOTS, labels } from "./chapters";
import InnerMirrorCard, { materializeCard } from "./InnerMirrorCard";
import InnerMirrorDpStep, { loadStoredDp } from "./InnerMirrorDpStep";

const INDIGO = "#5D00FF";
const AQUA = "#00C2CB";
const INK = "#1C1C1C";
const MUTE = "#676773";

function MirrorRing({ step }) {
  const total = 7;
  const r = 24;
  const circumference = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(total, step)) / total;
  return (
    <svg width="60" height="60" viewBox="0 0 60 60"
         aria-label={`Progress ${step} of ${total}`}
         data-testid="im-mirror-ring">
      <circle cx="30" cy="30" r={r} fill="none"
              stroke="rgba(93,0,255,0.12)" strokeWidth="3" />
      <circle cx="30" cy="30" r={r} fill="none"
              stroke={INDIGO} strokeWidth="3" strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - pct)}
              transform="rotate(-90 30 30)"
              style={{ transition: "stroke-dashoffset 420ms ease" }} />
      <circle cx="30" cy="30" r="4" fill={AQUA} />
    </svg>
  );
}

/** One chapter screen — single question, options, optional text. */
function ChapterScreen({ chapter, saved, onNext, onBack, canBack, submitting }) {
  const [selected, setSelected] = useState(saved?.selected || "");
  const [text, setText] = useState(saved?.text || "");
  useEffect(() => {
    setSelected(saved?.selected || "");
    setText(saved?.text || "");
  }, [chapter.n, saved?.selected, saved?.text]);
  const isSankalp = chapter.isSankalp;
  const canContinue = isSankalp
    ? ((text || "").trim().length > 0 || true) // skip allowed
    : !!selected;
  return (
    <div className="im-chapter kalki-dyn-text">
      <div className="im-eyebrow">{chapter.eyebrow}</div>
      <h1 className="im-h1">{chapter.title}</h1>
      <p className="im-sub">{chapter.sub}</p>
      {!isSankalp && (
        <div className="im-options" role="radiogroup">
          {chapter.options.map((o) => (
            <button key={o.k} type="button"
                    role="radio"
                    aria-checked={selected === o.k}
                    data-testid={`im-option-${chapter.n}-${o.k}`}
                    className={`im-option ${selected === o.k ? "is-sel" : ""}`}
                    onClick={() => setSelected(o.k)}>
              <span className="im-opt-icon">{o.icon}</span>
              <span className="im-opt-label">{o.label}</span>
            </button>
          ))}
        </div>
      )}
      {chapter.allowText && (
        <textarea
          data-testid={`im-text-${chapter.n}`}
          className="im-textarea"
          placeholder={chapter.textPlaceholder}
          maxLength={chapter.textMax || 300}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={isSankalp ? 5 : 3}
        />
      )}
      <div className="im-nav">
        {canBack && (
          <button type="button" className="im-ghost" data-testid="im-back"
                  onClick={onBack}>Back</button>
        )}
        <button type="button"
                className="im-primary"
                data-testid={`im-next-${chapter.n}`}
                disabled={!canContinue || submitting}
                onClick={() => onNext({
                  selected: isSankalp ? "submitted" : selected,
                  text: text || null,
                })}>
          {submitting ? "Saving…"
           : chapter.n === 7 ? "Complete My Reflection"
           : "Continue"}
        </button>
      </div>
      <div className="im-reflection">{chapter.reflection}</div>
    </div>
  );
}

/** Personalized summary + card + 5-slot invite + reward state. */
function CompletionScreen({ state, onRefresh }) {
  const navigate = useNavigate();
  const summary = state.summary || {};
  const invites = state.invites || [];
  const ackCount = invites.filter((i) => i.status === "acknowledged").length;
  const creditsEarned = Number(state?.progress?.credits_earned || 0);
  const creditsPerAck = Number(state?.progress?.credits_per_ack || 100);
  const creditsCap = Number(state?.progress?.credits_cap || 500);
  const siteOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const firstName = (state.user?.name || "").split(" ")[0] || "";
  const [dp, setDp] = useState(() => loadStoredDp(state.user?.user_id));
  // KALKI 1.3 — editable per-slot personalization: { recipient, message }.
  const [slotEdits, setSlotEdits] = useState({});
  const [openEditor, setOpenEditor] = useState(null); // slot number
  const updateEdit = (slot, patch) =>
    setSlotEdits((e) => ({ ...e, [slot]: { ...(e[slot] || {}), ...patch } }));

  // Inner Mirror 2.2 — toast "+100 KALKI Credits · Your Dharma Is Spreading"
  // whenever the server-authoritative credits_earned increases between
  // refreshes. The initial mount is skipped so returning users don't see
  // a stale toast for credits they already earned.
  const prevCreditsRef = React.useRef(creditsEarned);
  useEffect(() => {
    const prev = prevCreditsRef.current;
    if (creditsEarned > prev) {
      const delta = creditsEarned - prev;
      toast.success(`+${delta} KALKI Credits · Your Dharma Is Spreading.`);
    }
    prevCreditsRef.current = creditsEarned;
  }, [creditsEarned]);

  const startShare = useCallback(async (slot) => {
    const rel = RELATIONSHIP_SLOTS.find((s) => s.slot === slot);
    const edit = slotEdits[slot] || {};
    const recipient = (edit.recipient || "").trim().slice(0, 60);
    const defaultMsg = rel.message(firstName, recipient);
    const message = (edit.message ?? defaultMsg).slice(0, 1200);
    try {
      const { data } = await api.post("/inner-mirror/invite/share", {
        slot,
        relationship: rel.key,
        recipient_name: recipient || null,
        message,
      });
      const token = data?.token;
      if (!token) throw new Error("no_token");
      const link = `${siteOrigin}/inner-mirror/ack/${token}`;
      // KALKI 1.4 — share the Devotee Card PNG + personalized message
      // whenever the browser supports native file sharing (iOS Safari +
      // Android Chrome). Fallback: download the card and open wa.me so
      // the user can attach the image manually.
      const fullText = `${message}\n\n${link}`;
      let shared = false;
      try {
        const blob = await materializeCard({
          firstName: firstName || "A Devotee",
          badge: state.badge, foundingRank: state.founding_rank,
          motivation: labels.POSITIVE_MOTIVATIONS[summary.motivation],
          discovered: labels.MEMORY_LABEL[summary.memory]
                         || labels.FEELING_LABEL[summary.feeling],
          sankalp: summary.sankalp_text,
          publicCard: true,
          avatarDataUrl: dp?.dataUrl || null,
          avatarPublic: dp?.publicInclude !== false,
        });
        const file = new File([blob], "kalki-inner-mirror.png",
                                { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file], text: fullText,
            title: "KALKI — The Inner Mirror",
          });
          shared = true;
        }
      } catch (_) { /* fall through to wa.me text share */ }
      if (!shared) {
        const url = `https://wa.me/?text=${encodeURIComponent(fullText)}`;
        window.open(url, "_blank", "noopener");
      }
      await onRefresh();
      toast.success(shared
        ? "Card shared via WhatsApp."
        : "WhatsApp opened — attach the saved card to complete the share.");
    } catch (e) {
      toast.error("Could not prepare the share link.");
    }
  }, [onRefresh, firstName, siteOrigin, slotEdits, state.badge,
       state.founding_rank, summary.feeling, summary.memory,
       summary.motivation, summary.sankalp_text, dp?.dataUrl,
       dp?.publicInclude]);

  const selfConfirm = useCallback(async (slot) => {
    try {
      await api.post("/inner-mirror/self-confirm", { slot });
      await onRefresh();
      toast.success("Recorded — thank you.");
    } catch (e) { /* silent */ }
  }, [onRefresh]);

  // Inner Mirror 2.2 — the manual Claim path is retired. Credits now
  // flow in automatically via `ack_confirm`; this endpoint is left in
  // place as a server-side reconciler and is called best-effort on
  // mount so any outstanding balance for returning users is settled
  // without user action.
  useEffect(() => {
    (async () => {
      try { await api.post("/inner-mirror/claim-reward"); await onRefresh(); }
      catch (_) { /* silent — no credits owed */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="im-complete kalki-dyn-text">
      <div className="im-eyebrow">KALKI HAS HEARD YOU.</div>
      <h1 className="im-h1">Your experience is now part of our journey.</h1>
      <div className="im-sum-grid">
        <SumRow label="You came seeking"
                value={labels.POSITIVE_MOTIVATIONS[summary.motivation] || "—"} />
        <SumRow label="KALKI left you feeling"
                value={labels.FEELING_LABEL[summary.feeling] || "—"} />
        <SumRow label="Your strongest memory"
                value={labels.MEMORY_LABEL[summary.memory] || "—"} />
        <SumRow label="You hope KALKI becomes"
                value={labels.WISH_LABEL[summary.wish] || "—"} />
      </div>
      <p className="im-reflect-long">{summary.reflection}</p>

      <InnerMirrorDpStep
        userId={state.user?.user_id}
        userPicture={state.user?.picture}
        value={dp}
        onChange={setDp}
      />

      <InnerMirrorCard
        firstName={firstName || "A Devotee"}
        badge={state.badge}
        foundingRank={state.founding_rank}
        motivation={labels.POSITIVE_MOTIVATIONS[summary.motivation]}
        discovered={labels.MEMORY_LABEL[summary.memory]
                       || labels.FEELING_LABEL[summary.feeling]}
        sankalp={summary.sankalp_text}
        avatarDataUrl={dp?.dataUrl || null}
        avatarPublic={dp?.publicInclude !== false}
      />

      <section className="im-invites" data-testid="im-invites">
        <div className="im-eyebrow">🕉️  SPREAD THE LIGHT</div>
        <h2 className="im-h2">+100 KALKI Credits per friend · up to 500.</h2>
        <p className="im-sub">
          Each slot opens WhatsApp with your personal link. When five people acknowledge they received it, the challenge completes.
        </p>
        <div className="im-slot-progress"
             data-testid="im-slot-progress">
          <strong>{ackCount}</strong> of 5 Friends Recognized
          <span className="im-slot-progress-credits"
                 data-testid="im-slot-progress-credits">
            {" · "}
            <strong>{creditsEarned}</strong> / {creditsCap} KALKI Credits Earned
          </span>
        </div>
        <div className="im-slot-grid">
          {INVITE_SLOTS.map((s) => {
            const inv = invites.find((i) => i.slot === s.slot) || {};
            const done = inv.status === "acknowledged";
            const started = ["awaiting_ack", "share_started",
                               "self_confirmed", "acknowledged"].includes(inv.status);
            return (
              <div key={s.slot} className={`im-slot ${done ? "is-done" : ""}`}
                   data-testid={`im-slot-${s.slot}`}>
                <div className="im-slot-num">0{s.slot}</div>
                <div className="im-slot-label">{s.label}</div>
                <div className="im-slot-state">
                  {done ? "Acknowledged ✓"
                    : inv.status === "awaiting_ack" ? "Awaiting acknowledgment"
                    : inv.status === "self_confirmed" ? "Self-confirmed"
                    : "Ready"}
                </div>
                <button type="button" className="im-wa"
                        data-testid={`im-share-${s.slot}`}
                        onClick={() => startShare(s.slot)}>
                  {started ? "Share again" : "Invite on WhatsApp"}
                </button>
                {started && !done && (
                  <button type="button" className="im-ghost im-ghost-sm"
                          data-testid={`im-self-${s.slot}`}
                          onClick={() => selfConfirm(s.slot)}>
                    I shared this with someone
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {ackCount >= 5 ? (
          <div className="im-reward is-claimed" data-testid="im-reward-claimed">
            ❤️ {creditsEarned} KALKI Credits already added to your wallet.
          </div>
        ) : (
          <p className="im-note" data-testid="im-reward-note">
            Each friend who confirms they received your invite adds <strong>+{creditsPerAck} KALKI Credits</strong> to your wallet instantly. Sharing alone doesn't count — WhatsApp cannot confirm delivery.
          </p>
        )}
      </section>

      <div className="im-footer-row">
        <button className="im-primary"
                data-testid="im-explore"
                onClick={() => navigate("/app")}>
          Explore KALKI →
        </button>
        <button className="im-ghost"
                onClick={() => navigate("/")}>
          Back to home
        </button>
      </div>
    </div>
  );
}

function SumRow({ label, value }) {
  return (
    <div className="im-sum-row">
      <div className="im-sum-label">{label}</div>
      <div className="im-sum-val">{value}</div>
    </div>
  );
}

/** Guest intro — logged-out visitors land here. Branded hero with the
 *  "Begin Your Inner Mirror Journey" CTA that preserves /inner-mirror as
 *  the return URL through Google OAuth via useAuth.login(next). */
function GuestIntro({ onBegin }) {
  return (
    <div className="im-shell" data-testid="inner-mirror-guest">
      <KalkiGlobalHeader />
      <div className="im-top">
        <div>
          <div className="im-brandline">KALKI</div>
          <div className="im-brandsub">THE INNER MIRROR 🪞</div>
        </div>
      </div>
      <main className="im-main">
        <div className="im-intro-hero">
          <div className="im-eyebrow">KALKI · INNER MIRROR EXPERIENCE</div>
          <h1 className="im-h1" style={{ fontSize: "clamp(28px, 6vw, 44px)",
                                           textAlign: "center", margin: "14px 0 10px" }}>
            You experienced KALKI.<br />
            Now let KALKI understand you.
          </h1>
          <p className="im-sub" style={{ textAlign: "center", maxWidth: 520,
                                           margin: "4px auto 20px" }}>
            Seven moments of reflection. One journey toward a better tomorrow.
            A short conversation between you and KALKI — then a beautiful
            KALKI Devotee card to carry with you.
          </p>
          <div className="im-hero-ring" aria-hidden="true" />
          <div className="im-hero-actions">
            <button type="button"
                    data-testid="im-begin-journey"
                    className="im-primary"
                    onClick={onBegin}>
              Begin Your Inner Mirror Journey →
            </button>
            <p className="im-note">
              A quick sign-in keeps your reflections private — then you return right here.
            </p>
          </div>
          <ul className="im-hero-bullets">
            <li>🪞 Seven thoughtful chapters · ~90 seconds</li>
            <li>💠 Personalized KALKI Devotee card (1080×1920)</li>
            <li>❤️ Share with five people · earn 500 KALKI Credits</li>
          </ul>
        </div>
      </main>
      <style>{`
        .im-shell { min-height: 100dvh; background: #FFFFFF;
                     background-image: radial-gradient(rgba(93,0,255,0.05) 1px, transparent 1px);
                     background-size: 24px 24px; padding: 0 0 60px; }
        .im-top { display: flex; justify-content: space-between; align-items: center;
                   padding: 18px 24px 0; max-width: 820px; margin: 0 auto; }
        .im-brandline { font-size: 10px; letter-spacing: 0.36em; color: #5D00FF; font-weight: 500; }
        .im-brandsub  { font-size: 14px; letter-spacing: 0.14em; color: #1C1C1C;
                         margin-top: 4px; font-family: 'EB Garamond', serif; }
        .im-main  { max-width: 720px; margin: 0 auto; padding: 32px 24px 80px; min-width: 0; }
        .im-eyebrow { font-size: 10.5px; letter-spacing: 0.32em;
                       color: #5D00FF; font-weight: 500; text-transform: uppercase; text-align: center; }
        .im-h1 { font-family: 'EB Garamond', serif; line-height: 1.25; color: #1C1C1C; font-weight: 500; }
        .im-sub { color: #676773; font-size: 14px; line-height: 1.55; }
        .im-primary { display: inline-flex; align-items: center; gap: 8px;
                       background: linear-gradient(135deg, #5D00FF, #FF2E9A);
                       color: #FFF; border: 0; padding: 14px 26px; border-radius: 999px;
                       font-size: 12px; letter-spacing: 0.14em; cursor: pointer;
                       text-transform: uppercase; font-weight: 600; text-decoration: none; }
        .im-note { color: #676773; font-size: 11.5px; margin-top: 10px;
                    text-align: center; line-height: 1.5; max-width: 300px; }
        .im-intro-hero { padding: 24px 4px 48px; display: flex; flex-direction: column; align-items: center; }
        .im-hero-ring { width: 180px; height: 180px; border-radius: 50%;
                         border: 2px solid #5D00FF;
                         box-shadow: 0 0 0 10px rgba(0,194,203,0.14),
                                       0 24px 60px rgba(93,0,255,0.14);
                         margin: 10px auto 24px; }
        .im-hero-actions { display: flex; flex-direction: column; align-items: center;
                            gap: 10px; margin-top: 10px; }
        .im-hero-bullets { list-style: none; padding: 0; margin: 28px auto 0;
                            max-width: 420px; text-align: left; color: #1C1C1C;
                            font-size: 13.5px; line-height: 1.9; }
      `}</style>
    </div>
  );
}

export default function InnerMirrorApp() {
  const { user, loading: authLoading, login } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [chapterIndex, setChapterIndex] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/inner-mirror/me");
      setState({ ...data, user });
      // Position wizard at the server's current_chapter unless already
      // completed (jump straight to completion screen).
      if (!data.completed) {
        const next = (data.session?.current_chapter || 1) - 1;
        setChapterIndex(Math.max(0, Math.min(6, next)));
      }
    } catch (_) {}
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    refresh();
  }, [user, refresh]);
  const ensureSession = useCallback(async () => {
    if (state?.session) return;
    try { await api.post("/inner-mirror/session"); } catch (_) {}
  }, [state?.session]);

  const handleNext = useCallback(async ({ selected, text }) => {
    const chapter = CHAPTERS[chapterIndex];
    setSubmitting(true);
    try {
      await ensureSession();
      await api.post(`/inner-mirror/chapter/${chapter.n}`, { selected, text });
      if (chapter.n < 7) {
        setChapterIndex(chapterIndex + 1);
        await refresh();
      } else {
        await api.post("/inner-mirror/complete");
        await refresh();
      }
    } catch (e) {
      toast.error("Could not save that answer. Please try again.");
    } finally { setSubmitting(false); }
  }, [chapterIndex, ensureSession, refresh]);

  if (authLoading || (user && loading)) {
    return <div className="im-shell"><div className="im-loading">Loading your mirror…</div></div>;
  }
  if (!user) {
    // Guest intro — the branded /inner-mirror hero. Clicking Begin stashes
    // /inner-mirror as the post-login destination and kicks off Google auth.
    return <GuestIntro onBegin={() => login("/inner-mirror")} />;
  }
  // Inner Mirror 2.0 — the Waitlist gate has been removed. Every
  // authenticated KALKI user proceeds straight to the reflection flow.
  // Founding Devotees (first 110) still carry their special badge via
  // state.founding + state.founding_rank, but access is universal.

  const completed = state?.completed;
  const chapter = CHAPTERS[chapterIndex];
  const saved = state?.responses?.[chapter?.n] || {};

  return (
    <div className="im-shell" data-testid="inner-mirror">
      <KalkiGlobalHeader />
      <div className="im-top">
        <div>
          <div className="im-brandline">KALKI</div>
          <div className="im-brandsub">THE INNER MIRROR 🪞</div>
        </div>
        {!completed && <MirrorRing step={chapterIndex + (saved?.selected ? 1 : 0)} />}
      </div>

      <main className="im-main">
        {!completed && (
          <>
            <div className="im-intro">
              <p className="im-headline">You experienced KALKI. Now let KALKI understand you.</p>
              <p className="im-tag">Seven moments of reflection. One journey toward a better tomorrow.</p>
            </div>
            <ChapterScreen key={chapter.n}
                            chapter={chapter}
                            saved={saved}
                            canBack={chapterIndex > 0}
                            submitting={submitting}
                            onNext={handleNext}
                            onBack={() => setChapterIndex(Math.max(0, chapterIndex - 1))} />
          </>
        )}
        {completed && <CompletionScreen state={state} onRefresh={refresh} />}
      </main>

      <style>{`
        .im-shell { min-height: 100dvh; background: #FFFFFF;
                     background-image: radial-gradient(rgba(93,0,255,0.05) 1px, transparent 1px);
                     background-size: 24px 24px;
                     color: ${INK};
                     padding: 0 0 60px; }
        .im-top   { display: flex; justify-content: space-between;
                     align-items: center; padding: 18px 24px 0;
                     max-width: 820px; margin: 0 auto; }
        .im-brandline { font-size: 10px; letter-spacing: 0.36em;
                         color: ${INDIGO}; font-weight: 500; }
        .im-brandsub  { font-size: 14px; letter-spacing: 0.14em;
                         color: ${INK}; margin-top: 4px;
                         font-family: 'EB Garamond', serif; }
        .im-main  { max-width: 720px; margin: 0 auto;
                     padding: 32px 24px 80px;
                     min-width: 0; }
        .im-intro { text-align: center; margin-bottom: 36px; }
        .im-headline { font-family: 'EB Garamond', serif;
                        font-size: clamp(22px, 4.4vw, 32px);
                        line-height: 1.2; color: ${INK}; margin: 0; }
        .im-tag { color: ${MUTE}; font-size: 13.5px; margin-top: 10px; }
        .im-chapter { background: var(--kalki-snippet-surface);
                       border: 1px solid var(--kalki-snippet-border);
                       border-radius: 20px; padding: 28px 24px;
                       box-shadow: 0 12px 40px rgba(93,0,255,0.06); }
        .im-eyebrow { font-size: 10.5px; letter-spacing: 0.32em;
                       color: ${INDIGO}; font-weight: 500;
                       text-transform: uppercase; }
        .im-h1 { font-family: 'EB Garamond', serif;
                  font-size: clamp(22px, 4.6vw, 30px); line-height: 1.25;
                  margin: 8px 0 8px; color: ${INK}; font-weight: 500; }
        .im-h2 { font-family: 'EB Garamond', serif;
                  font-size: clamp(18px, 3.6vw, 22px); line-height: 1.3;
                  margin: 10px 0; color: ${INK}; font-weight: 500; }
        .im-sub { color: ${MUTE}; font-size: 14px; margin: 6px 0 20px;
                   line-height: 1.55; }
        .im-options { display: grid; gap: 10px; margin: 16px 0 10px; }
        .im-option { display: flex; align-items: center; gap: 12px;
                      padding: 12px 16px; border-radius: 14px;
                      border: 1px solid rgba(28,28,28,0.08);
                      background: rgba(255,255,255,0.5);
                      color: ${INK}; font-size: 14.5px; text-align: left;
                      cursor: pointer; transition: all 180ms ease;
                      min-width: 0; max-width: 100%; }
        .im-option.is-sel { border-color: ${INDIGO};
                             background: rgba(93,0,255,0.08);
                             box-shadow: 0 4px 18px rgba(93,0,255,0.14); }
        .im-opt-icon { font-size: 18px; width: 24px; text-align: center; }
        .im-opt-label { flex: 1; min-width: 0;
                         overflow-wrap: anywhere; }
        .im-textarea { width: 100%; max-width: 100%;
                        margin-top: 10px; padding: 12px 14px;
                        border-radius: 12px;
                        border: 1px solid rgba(28,28,28,0.1);
                        font-size: 14px; font-family: inherit;
                        resize: vertical; min-height: 72px;
                        box-sizing: border-box; min-width: 0; }
        .im-nav { display: flex; gap: 10px; margin-top: 18px;
                   justify-content: space-between; align-items: center;
                   flex-wrap: wrap; }
        .im-primary { background: linear-gradient(135deg, ${INDIGO}, #FF2E9A);
                       color: #FFF; border: 0; padding: 12px 22px;
                       border-radius: 999px; font-size: 12px;
                       letter-spacing: 0.14em; cursor: pointer;
                       text-transform: uppercase; font-weight: 600; }
        .im-primary:disabled { opacity: 0.55; cursor: not-allowed; }
        .im-ghost { background: transparent; color: ${MUTE};
                     border: 1px solid rgba(28,28,28,0.12);
                     padding: 11px 18px; border-radius: 999px;
                     cursor: pointer; font-size: 12px; letter-spacing: 0.1em; }
        .im-ghost-sm { padding: 7px 14px; font-size: 11px; }
        .im-reflection { color: ${MUTE}; font-style: italic;
                          font-size: 12.5px; margin-top: 18px;
                          text-align: center; }
        /* COMPLETION */
        .im-complete { padding: 8px 0 24px; }
        .im-sum-grid { display: grid; gap: 10px; margin: 20px 0 10px;
                        padding: 16px; border-radius: 16px;
                        background: var(--kalki-snippet-surface-astro);
                        border: 1px solid var(--kalki-snippet-border-astro); }
        .im-sum-row { display: flex; justify-content: space-between;
                       gap: 10px; font-size: 13.5px;
                       flex-wrap: wrap; min-width: 0; }
        .im-sum-label { color: ${MUTE}; }
        .im-sum-val { color: ${INK}; font-weight: 500;
                       text-align: right; overflow-wrap: anywhere;
                       min-width: 0; }
        .im-reflect-long { color: ${INK}; font-family: 'EB Garamond', serif;
                            font-size: 16px; line-height: 1.5;
                            margin: 16px 0 24px; }
        .im-invites { margin-top: 28px; padding: 22px 20px;
                       border-radius: 20px;
                       background: var(--kalki-snippet-surface-blessing);
                       border: 1px solid var(--kalki-snippet-border-blessing); }
        .im-slot-progress { font-size: 13.5px; color: ${INK};
                             margin: 10px 0 16px; }
        .im-slot-progress strong { font-size: 22px; color: ${INDIGO}; }
        .im-slot-grid { display: grid;
                         grid-template-columns: repeat(auto-fit,
                                                        minmax(min(100%, 220px), 1fr));
                         gap: 12px; }
        .im-slot { background: #FFFFFF; border-radius: 16px; padding: 16px;
                    border: 1px solid rgba(28,28,28,0.06);
                    display: flex; flex-direction: column; gap: 8px;
                    min-width: 0; max-width: 100%; }
        .im-slot.is-done { background: var(--kalki-snippet-surface-karma); }
        .im-slot-num { font-size: 10px; letter-spacing: 0.32em;
                        color: ${INDIGO}; font-weight: 500; }
        .im-slot-label { font-size: 13.5px; color: ${INK};
                          overflow-wrap: anywhere; min-width: 0; }
        .im-slot-theme { font-size: 11px; color: ${INDIGO};
                          font-style: italic; letter-spacing: 0.04em; }
        .im-slot-state { font-size: 11px; color: ${MUTE};
                          text-transform: uppercase; letter-spacing: 0.08em; }
        .im-slot-actions { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 2px; }
        .im-slot-editor { margin-top: 6px; padding: 12px; border-radius: 12px;
                           background: rgba(93,0,255,0.04);
                           border: 1px dashed rgba(93,0,255,0.14); }
        .im-ed-label { font-size: 10.5px; letter-spacing: 0.14em;
                        color: ${MUTE}; text-transform: uppercase; display: block; }
        .im-ed-input, .im-ed-textarea { width: 100%; max-width: 100%; min-width: 0;
                                         box-sizing: border-box; margin-top: 6px;
                                         padding: 10px 12px; border-radius: 10px;
                                         border: 1px solid rgba(28,28,28,0.1);
                                         font-size: 13px; font-family: inherit; }
        .im-ed-textarea { resize: vertical; min-height: 110px; line-height: 1.5; }
        .im-ed-row { display: flex; justify-content: space-between; margin-top: 8px; }
        .im-wa { background: #25D366; color: #FFF; border: 0;
                  padding: 10px 14px; border-radius: 999px;
                  font-size: 11.5px; letter-spacing: 0.08em;
                  cursor: pointer; font-weight: 600; }
        .im-claim { margin-top: 22px; }
        .im-reward.is-claimed { margin-top: 22px; padding: 14px 16px;
                                  border-radius: 14px; text-align: center;
                                  background: rgba(21,184,129,0.1);
                                  color: #0F7A5A; font-size: 13.5px; }
        .im-note { color: ${MUTE}; font-size: 12px; margin-top: 14px;
                    text-align: center; line-height: 1.5; }
        .im-footer-row { display: flex; gap: 10px; justify-content: center;
                          margin-top: 32px; flex-wrap: wrap; }
        .im-loading { padding: 80px 24px; text-align: center; color: ${MUTE}; }
      `}</style>
    </div>
  );
}
